import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPart, PartStatus } from "../../models/Part";
import usePartMutations from "../../data/usePartMutations";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useNotification from "../notifications/useNotifications";

export default function PartStatusDropdown({ part, classes = "" }: { part: IPart, classes?: string }) {
  const { t } = useTranslation("stock");
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { patchPart, deletePart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Status");
  const { deleteHandlers } = useDefaultCRUDHandlers("Part");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleStatusUpdate = (status: string) => {
    const newStatus = parseInt(status);

    // Deleted isn't a status you edit a part into - it's a delete. Confirm first, then go through
    // the real delete so Deleted/DeletedBy get stamped and any live listing comes down.
    if (newStatus === PartStatus.Deleted) {
      setConfirmDeleteOpen(true);
      return;
    }

    const updatedPart: IPart = {
      ...part,
      partStatus: newStatus,
    };

    patchPart.mutate(updatedPart, {
      onSuccess: () => {
        queryClient.invalidateQueries(["pagedTable"]);
        saveHandlers.onSuccess();
      },
      onError: (error) => {
        saveHandlers.onError();
      }
    });
  }

  const handleDelete = () => {
    deletePart.mutate(part.id, {
      onSuccess: (res) => {
        setConfirmDeleteOpen(false);

        if (res?.data && !res.data.listingWithdrawn) {
          addNotification({
            variant: "warn",
            primaryText: t("Part deleted, but it is still live on a marketplace"),
            secondaryText: part.tagNumber,
          });
          return;
        }

        deleteHandlers.onSuccess();
      },
    });
  };

  return (
    <div className="w-full h-full flex flex-col gap-4 items-center" onClick={(e) => e.stopPropagation()}>
      <ConfirmModal
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        title={t("Delete Part?")}
        message={t("Are you sure you want to delete this part?")}
        confirmButtonText={t("Delete")}
        onConfirm={handleDelete}
        isLoading={deletePart.isLoading}
      />
      <select value={part.partStatus} className={`${classes} border p-1`} onChange={(e) => handleStatusUpdate(e.target.value)}>
        {Object.values(PartStatus).filter((ct) => isNaN(Number(ct))).map((status, i) => (
          <option key={status} value={i}>
            {t(String(status))}
          </option>
        ))}
      </select>

      {part.partStatus === PartStatus.QC && part.qcReason && (
        <p className="text-xs text-red-500 ml-2">{part.qcReason}</p>
      )}
    </div>
  )
}
